import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Building2, Key, Mail, Phone, Globe, MessageCircle, Users, User, Upload, Trash2, ImageIcon, DollarSign, Clock } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

interface Agency {
  id: string;
  name: string;
  code: string;
  contact_email: string | null;
  plan: string;
  is_approved: boolean;
  created_at: string;
  updated_at: string;
  logo_url: string | null;
  custom_price_override: number | null;
  price_override_notes: string | null;
  address?: string | null;
  phone?: string | null;
  manager_name?: string | null;
  num_employees?: number | null;
  website?: string | null;
  whatsapp?: string | null;
  state_billing_rate?: number | null;
  caregiver_rate?: number | null;
  patient_care_hours?: number | null;
  _count?: {
    patients: number;
    users: number;
    sessions: number;
  };
}

type PlanType = string;

interface DbPlan {
  id: string;
  plan_key: string;
  name: string;
  price: number;
  patient_limit: number | null;
}

interface EditAgencyDialogProps {
  agency: Agency | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAgencyUpdated: () => void;
  isSuperAdmin?: boolean;
}

export default function EditAgencyDialog({
  agency,
  open,
  onOpenChange,
  onAgencyUpdated,
  isSuperAdmin = false,
}: EditAgencyDialogProps) {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);
  const [dbPlans, setDbPlans] = useState<DbPlan[]>([]);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [deletingLogo, setDeletingLogo] = useState(false);
  // Load plans from database
  useEffect(() => {
    const loadPlans = async () => {
      try {
        const { data, error } = await supabase
          .from('subscription_plans')
          .select('id, plan_key, name, price, patient_limit')
          .eq('is_active', true)
          .order('sort_order', { ascending: true });

        if (error) throw error;
        setDbPlans(data || []);
      } catch (error) {
        console.error('Error loading plans:', error);
      }
    };
    loadPlans();
  }, []);
  
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    contact_email: '',
    address: '',
    phone: '',
    manager_name: '',
    num_employees: '',
    website: '',
    whatsapp: '',
    plan: 'free' as PlanType,
    custom_price_override: '',
    price_override_notes: '',
    state_billing_rate: '',
    caregiver_rate: '',
    patient_care_hours: '',
  });

  useEffect(() => {
    if (agency) {
      setFormData({
        name: agency.name || '',
        code: agency.code || '',
        contact_email: agency.contact_email || '',
        address: agency.address || '',
        phone: agency.phone || '',
        manager_name: agency.manager_name || '',
        num_employees: agency.num_employees?.toString() || '',
        website: agency.website || '',
        whatsapp: agency.whatsapp || '',
        plan: agency.plan || 'free',
        custom_price_override: agency.custom_price_override?.toString() || '',
        price_override_notes: agency.price_override_notes || '',
        state_billing_rate: agency.state_billing_rate?.toString() || '',
        caregiver_rate: agency.caregiver_rate?.toString() || '',
        patient_care_hours: agency.patient_care_hours?.toString() || '',
      });
      setLogoUrl(agency.logo_url);
    }
  }, [agency]);

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !agency) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid File',
        description: 'Please select an image file (JPG, PNG, etc.)',
        variant: 'destructive',
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'File Too Large',
        description: 'Please select an image smaller than 5MB',
        variant: 'destructive',
      });
      return;
    }

    setUploadingLogo(true);
    try {
      // Generate unique file name
      const fileExt = file.name.split('.').pop();
      const fileName = `${agency.id}-${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;

      // Delete old logo if exists
      if (logoUrl) {
        const oldPath = logoUrl.split('/').pop();
        if (oldPath) {
          await supabase.storage.from('agency-logos').remove([oldPath]);
        }
      }

      // Upload new logo
      const { error: uploadError } = await supabase.storage
        .from('agency-logos')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('agency-logos')
        .getPublicUrl(filePath);

      const newLogoUrl = urlData.publicUrl;

      // Update agency record
      const { error: updateError } = await supabase
        .from('agencies')
        .update({ logo_url: newLogoUrl })
        .eq('id', agency.id);

      if (updateError) throw updateError;

      setLogoUrl(newLogoUrl);
      toast({
        title: 'Logo Uploaded',
        description: 'Agency logo has been updated successfully.',
      });
    } catch (error: any) {
      console.error('Error uploading logo:', error);
      toast({
        title: 'Upload Failed',
        description: error.message || 'Failed to upload logo',
        variant: 'destructive',
      });
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!agency || !logoUrl) return;

    setDeletingLogo(true);
    try {
      // Delete from storage
      const fileName = logoUrl.split('/').pop();
      if (fileName) {
        await supabase.storage.from('agency-logos').remove([fileName]);
      }

      // Update agency record
      const { error } = await supabase
        .from('agencies')
        .update({ logo_url: null })
        .eq('id', agency.id);

      if (error) throw error;

      setLogoUrl(null);
      toast({
        title: 'Logo Removed',
        description: 'Agency logo has been removed.',
      });
    } catch (error: any) {
      console.error('Error deleting logo:', error);
      toast({
        title: 'Delete Failed',
        description: error.message || 'Failed to remove logo',
        variant: 'destructive',
      });
    } finally {
      setDeletingLogo(false);
    }
  };

  const handleSave = async () => {
    if (!agency) return;
    
    setSaving(true);
    try {
      const updateData: Record<string, any> = {
        name: formData.name.trim(),
        code: formData.code.trim().toUpperCase(),
        contact_email: formData.contact_email.trim() || null,
        address: formData.address.trim() || null,
        phone: formData.phone.trim() || null,
        manager_name: formData.manager_name.trim() || null,
        num_employees: formData.num_employees ? parseInt(formData.num_employees) : null,
        website: formData.website.trim() || null,
        whatsapp: formData.whatsapp.trim() || null,
        plan: formData.plan,
        custom_price_override: formData.custom_price_override ? parseFloat(formData.custom_price_override) : null,
        price_override_notes: formData.price_override_notes.trim() || null,
        state_billing_rate: formData.state_billing_rate ? parseFloat(formData.state_billing_rate) : null,
        caregiver_rate: formData.caregiver_rate ? parseFloat(formData.caregiver_rate) : null,
        patient_care_hours: formData.patient_care_hours ? parseFloat(formData.patient_care_hours) : null,
      };

      const { error } = await supabase
        .from('agencies')
        .update(updateData)
        .eq('id', agency.id);

      if (error) throw error;

      toast({
        title: 'Agency Updated',
        description: `${formData.name} has been updated successfully.`,
      });

      onAgencyUpdated();
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error updating agency:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update agency',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSendPasswordReset = async () => {
    if (!formData.contact_email) {
      toast({
        title: 'No Email',
        description: 'Please enter a contact email first.',
        variant: 'destructive',
      });
      return;
    }

    setSendingReset(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(formData.contact_email, {
        redirectTo: `${window.location.origin}/admin/reset-password`,
      });

      if (error) throw error;

      toast({
        title: 'Password Reset Sent',
        description: `A password reset link has been sent to ${formData.contact_email}.`,
      });
    } catch (error: any) {
      console.error('Error sending password reset:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to send password reset email',
        variant: 'destructive',
      });
    } finally {
      setSendingReset(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Edit Agency
          </DialogTitle>
          <DialogDescription>
            Update agency profile and settings
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Logo Upload */}
          <div className="space-y-4">
            <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Agency Logo</h4>
            <div className="flex items-center gap-6">
              <Avatar className="h-24 w-24 border-2 border-border">
                <AvatarImage src={logoUrl || undefined} alt={formData.name || 'Agency logo'} />
                <AvatarFallback className="bg-muted">
                  <ImageIcon className="h-10 w-10 text-muted-foreground" />
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 space-y-3">
                <div className="flex items-center gap-2">
                  <Label htmlFor="logo-upload" className="cursor-pointer">
                    <div className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground transition-colors">
                      {uploadingLogo ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4" />
                          Upload Logo
                        </>
                      )}
                    </div>
                    <Input
                      id="logo-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      disabled={uploadingLogo}
                      className="hidden"
                    />
                  </Label>
                  {logoUrl && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDeleteLogo}
                      disabled={deletingLogo}
                      className="text-destructive hover:text-destructive"
                    >
                      {deletingLogo ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Recommended: Square image, at least 200x200 pixels. Max 5MB.
                </p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Basic Information */}
          <div className="space-y-4">
            <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Basic Information</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Agency Name *</Label>
                <Input
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Agency name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-code">Agency Code</Label>
                <Input
                  id="edit-code"
                  value={formData.code}
                  onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                  placeholder="ABC001"
                  maxLength={10}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-address">Address</Label>
              <Textarea
                id="edit-address"
                value={formData.address}
                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                placeholder="Full address"
                rows={2}
              />
            </div>
          </div>

          <Separator />

          {/* Contact Information */}
          <div className="space-y-4">
            <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Contact Information</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-email" className="flex items-center gap-1.5">
                  <Mail className="h-3.5 w-3.5" />
                  Email
                </Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_email: e.target.value }))}
                  placeholder="contact@agency.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-phone" className="flex items-center gap-1.5">
                  <Phone className="h-3.5 w-3.5" />
                  Phone
                </Label>
                <Input
                  id="edit-phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="+1 (555) 123-4567"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-website" className="flex items-center gap-1.5">
                  <Globe className="h-3.5 w-3.5" />
                  Website
                </Label>
                <Input
                  id="edit-website"
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                  placeholder="https://www.agency.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-whatsapp" className="flex items-center gap-1.5">
                  <MessageCircle className="h-3.5 w-3.5" />
                  WhatsApp
                </Label>
                <Input
                  id="edit-whatsapp"
                  type="tel"
                  value={formData.whatsapp}
                  onChange={(e) => setFormData(prev => ({ ...prev, whatsapp: e.target.value }))}
                  placeholder="+1 (555) 123-4567"
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Staff Information */}
          <div className="space-y-4">
            <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Staff Information</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-manager" className="flex items-center gap-1.5">
                  <User className="h-3.5 w-3.5" />
                  Manager Name
                </Label>
                <Input
                  id="edit-manager"
                  value={formData.manager_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, manager_name: e.target.value }))}
                  placeholder="John Doe"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-employees" className="flex items-center gap-1.5">
                  <Users className="h-3.5 w-3.5" />
                  Number of Employees
                </Label>
                <Input
                  id="edit-employees"
                  type="number"
                  min="0"
                  value={formData.num_employees}
                  onChange={(e) => setFormData(prev => ({ ...prev, num_employees: e.target.value }))}
                  placeholder="10"
                />
              </div>
            </div>
            <div className="p-3 bg-muted/50 rounded-lg">
              <p className="text-sm text-muted-foreground">
                <Users className="h-4 w-4 inline mr-1.5" />
                <strong>{agency?._count?.patients || 0}</strong> patients registered
              </p>
            </div>
          </div>

          <Separator />

          {/* Billing & Plan - Only show plan editing to super admins */}
          <div className="space-y-4">
            <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Billing & Subscription</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-plan">Subscription Plan</Label>
                {isSuperAdmin ? (
                  <Select
                    value={formData.plan}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, plan: value as PlanType }))}
                  >
                    <SelectTrigger id="edit-plan">
                      <SelectValue placeholder="Select plan" />
                    </SelectTrigger>
                    <SelectContent>
                      {dbPlans.length > 0 ? (
                        dbPlans.map((plan) => (
                          <SelectItem key={plan.id} value={plan.plan_key}>
                            {plan.name} - ${plan.price}/mo ({plan.patient_limit?.toLocaleString() || 'Unlimited'} patients)
                          </SelectItem>
                        ))
                      ) : (
                        <>
                          <SelectItem value="free">Free - $0/mo</SelectItem>
                          <SelectItem value="startup">StartUp - $199/mo</SelectItem>
                          <SelectItem value="mid_level">Mid Level - $399/mo</SelectItem>
                          <SelectItem value="enterprise">Enterprise - $799/mo</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="p-2 border rounded-md bg-muted/50 text-sm capitalize">
                    {formData.plan.replace('_', ' ')}
                  </div>
                )}
              </div>
              {isSuperAdmin && (
                <div className="space-y-2">
                  <Label htmlFor="edit-price-override">Custom Price Override</Label>
                  <Input
                    id="edit-price-override"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.custom_price_override}
                    onChange={(e) => setFormData(prev => ({ ...prev, custom_price_override: e.target.value }))}
                    placeholder="Leave empty for standard pricing"
                  />
                </div>
              )}
            </div>
            {isSuperAdmin && (
              <div className="space-y-2">
                <Label htmlFor="edit-price-notes">Price Override Notes</Label>
                <Textarea
                  id="edit-price-notes"
                  value={formData.price_override_notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, price_override_notes: e.target.value }))}
                  placeholder="Reason for custom pricing..."
                  rows={2}
                />
              </div>
            )}
            {!isSuperAdmin && (
              <p className="text-sm text-muted-foreground">
                Contact your administrator to change your subscription plan.
              </p>
            )}
          </div>

          <Separator />

          {/* Billing & Reimbursement Settings */}
          <div className="space-y-4">
            <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Billing & Reimbursement Settings</h4>
            <p className="text-sm text-muted-foreground">
              Configure agency-specific billing rates used for state reimbursement calculations.
            </p>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-state-billing-rate" className="flex items-center gap-1.5">
                  <DollarSign className="h-3.5 w-3.5" />
                  State Billing Rate ($/hr)
                </Label>
                <Input
                  id="edit-state-billing-rate"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.state_billing_rate}
                  onChange={(e) => setFormData(prev => ({ ...prev, state_billing_rate: e.target.value }))}
                  placeholder="e.g., 25.00"
                />
                <p className="text-xs text-muted-foreground">
                  Hourly rate billed to Medicaid/Medicare
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-caregiver-rate" className="flex items-center gap-1.5">
                  <DollarSign className="h-3.5 w-3.5" />
                  Caregiver Rate ($/hr)
                </Label>
                <Input
                  id="edit-caregiver-rate"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.caregiver_rate}
                  onChange={(e) => setFormData(prev => ({ ...prev, caregiver_rate: e.target.value }))}
                  placeholder="e.g., 15.00"
                />
                <p className="text-xs text-muted-foreground">
                  Hourly rate paid to caregivers
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-patient-care-hours" className="flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5" />
                  Patient Care Hours
                </Label>
                <Input
                  id="edit-patient-care-hours"
                  type="number"
                  min="0"
                  step="0.5"
                  value={formData.patient_care_hours}
                  onChange={(e) => setFormData(prev => ({ ...prev, patient_care_hours: e.target.value }))}
                  placeholder="e.g., 40"
                />
                <p className="text-xs text-muted-foreground">
                  Approved weekly care hours
                </p>
              </div>
            </div>
            <div className="p-3 bg-muted/50 rounded-lg">
              <p className="text-xs text-muted-foreground">
                <strong>Note:</strong> These rates are set by the Department of Aging based on patient care requirements. 
                Billing stops when patients enter ER/hospital and resumes upon discharge.
              </p>
            </div>
          </div>

          <Separator />

          {/* Password Reset */}
          <div className="space-y-4">
            <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Account Actions</h4>
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="font-medium flex items-center gap-2">
                  <Key className="h-4 w-4" />
                  Password Reset
                </p>
                <p className="text-sm text-muted-foreground">
                  Send a password reset link to the agency contact email
                </p>
              </div>
              <Button
                variant="outline"
                onClick={handleSendPasswordReset}
                disabled={sendingReset || !formData.contact_email}
              >
                {sendingReset && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Send Reset Link
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || !formData.name.trim()}>
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
